import isEqual from 'lodash/isEqual'

import { WorkorderStatus } from '../../models'
import {
    AssignmentFilter,
    InspectionCompletionFilter,
    InfrastructureFilterActionTypes,
    InfrastructureFilterOptions,
    InfrastructureFilterState,
    SET_INFRASTRUCTURE_FILTER,
    InspectionHandledFilter,
    CalendarFilterMode,
    ChangeOrderFilterMode,
    PartialFilterMode,
    KeysFilterMode,
} from './types'

export const defaultFilterState: InfrastructureFilterOptions = {
    serviceFilter: [],
    vendorFilter: [],
    unitConfigFilter: [],
    invConfigFilter: [],
    vacantFilter: false,
    priorityFilter: false,
    unreadFilter: false,
    assignmentFilter: AssignmentFilter.All,
    inspectionCompletionFilter: InspectionCompletionFilter.All,
    hasDamages: false,
    hasMessages: false,
    manualServiceAreas: false,
    isGhost: false,
    hasGoBack: false,
    hasFlaggedItem: false,
    hasCostDriver: false,
    behindWorkorders: false,
    areaStatusFilter: {
        filterLength: 0,
    },
    statusFilter: {
        ASSIGNED: false,
        IN_PROGRESS: false,
        PAUSE: false,
        COMPLETE: false,
        GO_BACK: false,
        APPROVED: false,
        PRE_APPROVED: false,
        INVOICED: false,
        INVOICED_OUT: false,
        INVOICE_REQUEST: false,
    },
    customStatusFilter: {},
    dateFilter: {
        enable: false,
        filterMode: CalendarFilterMode.Overlap,
        filterLeases: false,
        filterServices: true,
        range: {
            startDate: new Date(),
            endDate: new Date(),
            key: 'date-range',
        },
    },
    unitSearch: '',
    handled: InspectionHandledFilter.All,
    changeOrder: ChangeOrderFilterMode.All,
    readyUnitFilter: {
        enabled: false,
        value: 'APPROVED' as WorkorderStatus,
    },
    partialUnitFilter: PartialFilterMode.All,
    displayVacantAreas: false,
    keysFilter: KeysFilterMode.All,
    serviceCategoryFilter: null,
    unassignedOccAreas: null,
    costFilter: {
        cost: null,
        operator: 'eq',
    },
    doubleTransferInFilter: false,
    allKeysTurnedInFilter: false,
}

const initialState: InfrastructureFilterState = {
    filter: defaultFilterState,
}

export const infrastructureFilterReducer = (
    state = initialState,
    action: InfrastructureFilterActionTypes,
): InfrastructureFilterState => {
    switch (action.type) {
        case SET_INFRASTRUCTURE_FILTER: {
            if (action.newFilter == null) {
                return {
                    ...state,
                    filter: defaultFilterState,
                }
            }

            const mergedFilter = {
                ...state.filter,
                ...action.newFilter,
            }

            if (isEqual(state.filter, mergedFilter)) {
                return state
            }

            let newAssignmentFilter: AssignmentFilter =
                state.filter.assignmentFilter

            const assignmentExplicitlyChanged =
                'assignmentFilter' in action.newFilter &&
                action.newFilter.assignmentFilter !==
                    state.filter.assignmentFilter

            if (assignmentExplicitlyChanged) {
                newAssignmentFilter =
                    action.newFilter.assignmentFilter ?? AssignmentFilter.All
            } else if (
                !('unassignedOccAreas' in action.newFilter) ||
                action.newFilter.unassignedOccAreas ===
                    state.filter.unassignedOccAreas
            ) {
                // If other filters are changing, set to Assigned
                newAssignmentFilter = AssignmentFilter.Assigned
            }

            if (
                'unitSearch' in action.newFilter &&
                action.newFilter.unitSearch !== state.filter.unitSearch
            ) {
                newAssignmentFilter = AssignmentFilter.All
            }

            return {
                ...state,
                filter: {
                    ...mergedFilter,
                    assignmentFilter: newAssignmentFilter,
                },
            }
        }

        default:
            return state
    }
}
